import { useMemo, useCallback } from "react";
import styled from "styled-components";
import BaseChart from "../../baseChart";
import type { EChartsOption } from "echarts";
import { numFormatter } from "../../../helpers/utils";

interface StackedBarChartWithDrillDownProps {
  data: {
    type: string;
    title: string;
    config: {
      currency_formatter: string;
      visual: {
        value_configs: Array<{
          name: string;
          accessor: string;
          aggregation_type: string;
          is_monetary_value_type: boolean;
        }>;
        category_config: {
          id_accessor: string;
          name: string;
          accessor: string;
        };
        stack_config: {
          id_accessor: string;
          name: string;
          accessor: string;
        };
        orientation: "horizontal" | "vertical";
        show_legends?: boolean;
        show_stack_labels?: boolean;
      };
    };
    data: any[];
  };
  width: number;
  height: number;
  selectedDimension: { key: string; name: string } | null;
  onDataPointClick?: (dataPoint: any) => void;
}

const StackedBarChartWithDrillDown = ({
  data,
  width,
  height,
  selectedDimension,
  onDataPointClick,
}: StackedBarChartWithDrillDownProps) => {
  // Get the value accessor based on selected dimension
  const valueAccessor = selectedDimension?.key || data.config.visual.value_configs[0]?.accessor || "sales";

  // Check if selected value is monetary
  const isMonetary = useMemo(() => {
    const config = data.config.visual.value_configs.find(
      (v) => v.accessor === valueAccessor
    );
    return config?.is_monetary_value_type ?? true;
  }, [data.config.visual.value_configs, valueAccessor]);

  // Transform data for ECharts
  const chartData = useMemo(() => {
    if (!data.data || !Array.isArray(data.data)) return { categories: [], values: [], rawData: [] };

    const categoryAccessor = data.config.visual.category_config.accessor;

    const categories = data.data.map((item) => item[categoryAccessor] || "Unknown");
    const values = data.data.map((item) => {
      const val = item[valueAccessor];
      return typeof val === "number" ? val : parseFloat(val) || 0;
    });

    return { categories, values, rawData: data.data };
  }, [data, valueAccessor]);

  // Build ECharts options
  const option = useMemo<EChartsOption>(() => {
    const { categories, values } = chartData;
    const isHorizontal = data.config.visual.orientation === "horizontal";
    const formatter = data.config.currency_formatter;

    // Determine bar color based on if clickable
    const barColor = onDataPointClick
      ? {
          type: 'linear' as const,
          x: 0,
          y: 0,
          x2: isHorizontal ? 1 : 0,
          y2: isHorizontal ? 0 : 1,
          colorStops: [
            { offset: 0, color: '#0f80d1' },
            { offset: 1, color: '#4da6e8' }
          ]
        }
      : '#6b7280';

    const axisLabelFormatter = (value: number) => {
      const formatted = numFormatter(value);
      return isMonetary ? `${formatted} ${formatter}` : formatted;
    };

    const tooltipFormatter = (params: any) => {
      // params is an array when trigger is "axis"
      const item = Array.isArray(params) ? params[0] : params;
      if (!item) return '';

      const categoryName = item.name || item.axisValue || 'Unknown';
      const value = item.data?.value ?? item.value ?? 0;
      const formattedValue = isMonetary
        ? `${numFormatter(value)} ${formatter}`
        : numFormatter(value);

      return `
        <div style="padding: 8px 12px;">
          <div style="font-weight: 600; margin-bottom: 4px;">${categoryName}</div>
          <div style="display: flex; align-items: center; gap: 8px;">
            ${item.marker || ''}
            <span>${selectedDimension?.name || "Value"}: </span>
            <span style="font-weight: 600;">${formattedValue}</span>
          </div>
          ${onDataPointClick ? '<div style="margin-top: 6px; font-size: 11px; color: #666;">Click to drill down</div>' : ''}
        </div>
      `;
    };

    const baseOption: EChartsOption = {
      tooltip: {
        show: true,
        trigger: "axis",
        axisPointer: {
          type: "shadow",
        },
        formatter: tooltipFormatter,
      },
      grid: {
        left: isHorizontal ? "15%" : "10%",
        right: "5%",
        top: "5%",
        bottom: isHorizontal ? "10%" : "15%",
        containLabel: true,
      },
      xAxis: isHorizontal
        ? {
            type: "value",
            axisLabel: { formatter: axisLabelFormatter },
          }
        : {
            type: "category",
            data: categories,
            axisLabel: {
              rotate: categories.length > 5 ? 30 : 0,
              interval: 0,
              width: 80,
              overflow: "truncate",
            },
            axisTick: { alignWithLabel: true },
          },
      yAxis: isHorizontal
        ? {
            type: "category",
            data: categories,
            axisLabel: {
              width: 100,
              overflow: "truncate",
            },
          }
        : {
            type: "value",
            axisLabel: { formatter: axisLabelFormatter },
          },
      series: [
        {
          type: "bar",
          data: values.map((value, index) => ({
            value,
            itemStyle: {
              color: barColor,
              borderRadius: isHorizontal ? [0, 4, 4, 0] : [4, 4, 0, 0],
            },
            // Store the raw data for click handling
            _rawData: chartData.rawData[index],
          })),
          barMaxWidth: 50,
          emphasis: onDataPointClick
            ? {
                itemStyle: {
                  color: '#0a5a94',
                  shadowBlur: 10,
                  shadowColor: 'rgba(15, 128, 209, 0.3)',
                },
              }
            : undefined,
          cursor: onDataPointClick ? "pointer" : "default",
        },
      ],
    };

    return baseOption;
  }, [chartData, data.config, isMonetary, selectedDimension, onDataPointClick]);

  // Handle click on chart
  const handleClick = useCallback(
    (params: any) => {
      if (!onDataPointClick) return;

      // Get the raw data from the clicked bar
      const rawData = params.data?._rawData || chartData.rawData[params.dataIndex];

      if (rawData) {
        onDataPointClick(rawData);
      }
    },
    [onDataPointClick, chartData.rawData]
  );

  if (width <= 0 || height <= 0) return null;

  return (
    <ChartWrapper $clickable={Boolean(onDataPointClick)}>
      <BaseChart
        width={width}
        height={height}
        options={option}
        chartType="bar"
        onClick={handleClick}
      />
    </ChartWrapper>
  );
};

const ChartWrapper = styled.div<{ $clickable: boolean }>`
  width: 100%;
  height: 100%;
  cursor: ${({ $clickable }) => ($clickable ? "pointer" : "auto")};
`;

export default StackedBarChartWithDrillDown;
